import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
  alpha,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Case } from '../../../types';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { toast } from 'react-toastify';
import EditCaseAmountDialog from './EditCaseAmountDialog';
import EditPaymentDialog from './EditPaymentDialog';

interface PaymentHistoryProps {
  cases: Case[];
}

const PaymentHistory: React.FC<PaymentHistoryProps> = ({ cases }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [editingCaseId, setEditingCaseId] = useState<string | null>(null);
  const [editingPayment, setEditingPayment] = useState<{
    payment: { id: string; date: string; amount: number; caseId: string };
    caseData: Case;
  } | null>(null);

  // Process payments and calculate totals
  const processPaymentsAndTotals = () => {
    let totalCharged = 0;
    let totalPaid = 0;
    const allPayments: Array<{
      id: string;
      date: string;
      amount: number;
      caseType: string;
      receiptNumber: string;
      caseId: string;
    }> = [];

    cases.forEach((caseItem) => {
      totalCharged += caseItem.amountCharged;

      // Add all payments
      if (caseItem.payments && caseItem.payments.length > 0) {
        caseItem.payments.forEach((payment) => {
          allPayments.push({
            ...payment,
            caseType: caseItem.type,
            receiptNumber: caseItem.receiptNumber,
          });
          totalPaid += payment.amount;
        });
      }
    });

    return {
      totalCharged,
      totalPaid,
      outstandingBalance: totalCharged - totalPaid,
      allPayments: allPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    };
  };

  const { totalCharged, totalPaid, outstandingBalance, allPayments } = processPaymentsAndTotals();

  const handleEditPayment = (payment: { id: string; date: string; amount: number; caseId: string }) => {
    const caseData = cases.find(c => c.id === payment.caseId);
    if (caseData) {
      setEditingPayment({ payment, caseData });
    }
  };

  const handleEditCase = (caseId: string) => {
    const caseData = cases.find(c => c.id === caseId);
    if (caseData) {
      setEditingCaseId(caseId);
    } else {
      toast.error('Case not found');
    }
  };

  // Mobile Payment List
  const MobilePaymentList = () => (
    <Stack spacing={2}>
      {allPayments.map((payment) => (
        <Card key={payment.id} sx={{ bgcolor: 'background.default' }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" color="primary">
                  {formatCurrency(payment.amount)}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleEditPayment(payment)}
                  sx={{
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Divider />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Date: {formatDate(payment.date)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Case: {payment.caseType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Receipt: {payment.receiptNumber}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  // Desktop Payment Table
  const DesktopPaymentTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Case Type</TableCell>
            <TableCell>Receipt Number</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allPayments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>{formatDate(payment.date)}</TableCell>
              <TableCell>{formatCurrency(payment.amount)}</TableCell>
              <TableCell>{payment.caseType}</TableCell>
              <TableCell>{payment.receiptNumber}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={() => handleEditPayment(payment)}
                  sx={{
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3, bgcolor: 'background.default' }}>
        <Stack spacing={3}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Charged
              </Typography>
              <Typography variant="h5" color="primary.main" fontWeight={600}>
                {formatCurrency(totalCharged)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Paid
              </Typography>
              <Typography variant="h5" color="success.main" fontWeight={600}>
                {formatCurrency(totalPaid)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Outstanding Balance
              </Typography>
              <Typography
                variant="h5"
                color={outstandingBalance > 0 ? 'error.main' : 'success.main'}
                fontWeight={600}
              >
                {formatCurrency(outstandingBalance)}
              </Typography>
            </Box>
          </Stack>

        </Stack>
      </Paper>

      {isMobile ? <MobilePaymentList /> : <DesktopPaymentTable />}

      {editingPayment && (
        <EditPaymentDialog
          open={!!editingPayment}
          onClose={() => setEditingPayment(null)}
          payment={editingPayment.payment}
          caseData={editingPayment.caseData}
          onPaymentUpdated={() => {
            setEditingPayment(null);
            window.location.reload();
          }}
        />
      )}

      {editingCaseId && (
        <EditCaseAmountDialog
          open={!!editingCaseId}
          onClose={() => setEditingCaseId(null)}
          caseData={cases.find(c => c.id === editingCaseId) || null}
          onAmountUpdated={() => {
            setEditingCaseId(null);
            window.location.reload();
          }}
        />
      )}
    </Box>
  );
};

export default PaymentHistory; 